import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { setCredentials, logout } from './store/slices/authSlice'
import { authService } from './services/authService'
import { isOwnerRole } from './utils/roles'
import Login from './pages/login/login'
import ResetPassword from './pages/reset-password/reset-password'
import PasswordSuccess from './pages/reset-password/password-success'
import Dashboard from './pages/dashboard/dashboard'
import MarketAnalysis from './pages/market-analysis/market-analysis'
import Users from './pages/users/users'
import AddAccount from './pages/users/add-account'
import CreateAccount from './pages/users/create-account'
import AssignAgent from './pages/assign-agent/assign-agent'
import AccountStatement from './pages/accounts/account-statement'
import ClientsAccountStatement from './pages/accounts/clients-account-statement'
import GeneralReport from './pages/reports/general-report'
import CasinoReport from './pages/reports/casino-report'
import ProfitLoss from './pages/reports/profit-loss'
import CasinoResultReport from './pages/reports/casino-result-report'
import GameReport from './pages/reports/game-report'
import CurrentBets from './pages/reports/current-bets'
import GeneralLock from './pages/settings/general-lock'
import SecureAuth from './pages/secure-auth/secure-auth'
import SecurityCodeVerification from './pages/security/security-verification'
import ChangePassword from './pages/settings/change-password'
import MultiLogin from './pages/multi-login/multi-login'
// import GameDetails from './pages/game-details/game-details' // Replaced with Sports
import Sports from './pages/sports'
import ProtectedRoute from './components/ProtectedRoute'
import Loader from './components/Loader'
import './App.css'
import './styles/toast-override.css'
import Users2 from './pages/users2/users2'
import { SidebarProvider } from './context/SidebarContext'
import DashboardHome from './pages/dashboardHome'
import AgentList from './pages/agent-listing'
import Transfer from './pages/transfer'
import Notification from './pages/notification'
import NetExposure from './pages/net-exposure'
import BetTicker from './pages/bet-ticker'
import BetList from './pages/bet-list'
import BufferPage from './pages/buffer'
import MatchSettlement from './pages/match-settlement'
import FancySettlement from './pages/fancy-settlement'
import VoidBets from './pages/void-bets'

import TransferStatement from './pages/transfer-statement'
import PLReport from './pages/profit-loss'
import MessageReport from './pages/reports/message-report'
import AgentCreate from './pages/agent-create'
import UserDetails from './pages/user-details'
import LiveCasino from './pages/liveCasino'
import CasinoConfig from './pages/casino-config'
import TeenPatti20c from './pages/lordGames/teen20c'
import Race17 from './pages/lordGames/race17'
import Dum10 from './pages/lordGames/dum10'
import Baccarat29 from './pages/lordGames/baccarat29'
import TeenMuf from './pages/lordGames/teenmuf'
import Lucky15 from './pages/lordGames/lucky15'
import TeenJoker from './pages/lordGames/teenjoker'
import Poker from './pages/lordGames/poker'
import Poker20 from './pages/lordGames/poker20'
import Card32 from './pages/lordGames/card32'
import Card32B from './pages/lordGames/card32b'
import Aaa from './pages/lordGames/aaa'
import Dt6 from './pages/lordGames/dt6'
import Dt20 from './pages/lordGames/dt20'
import CardJ from './pages/lordGames/3cardj'
import Worli from './pages/lordGames/worli'
import WorliMatka from './pages/lordGames/worliMatka'
import BTable from './pages/lordGames/btable'
import AB20 from './pages/lordGames/ab20'
import Sicbo from './pages/lordGames/sicbo'
import Race20 from './pages/lordGames/race20'
import Queen from './pages/lordGames/queen'
import ABJ from './pages/lordGames/abj'
import DT202 from './pages/lordGames/dt202'
import Baccarat2 from './pages/lordGames/baccarat2'
import Baccarat from './pages/lordGames/baccarat'
import Lucky7B from './pages/lordGames/lucky7b'
import Lucky7 from './pages/lordGames/lucky7'
import Cmeter from './pages/lordGames/cmeter'
import Cmatch20 from './pages/lordGames/cmatch20'
import CasinoWar from './pages/lordGames/casinoWar'
import DragonTigerLion from './pages/lordGames/dragonTigerLion'
import Teen9 from './pages/lordGames/teen9'
import Teen from './pages/lordGames/teen'
import Teen20 from './pages/lordGames/teen20'
import Teen8 from './pages/lordGames/teen8'

/**
 * App Component
 * 
 * Main application component that sets up routing and layout.
 */
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  )
}

/**
 * AppContent Component
 * 
 * Handles route-based body class management and routing logic.
 */
function AppContent() {
  const location = useLocation()
  const dispatch = useDispatch()
  const { isAuthenticated, token, user } = useSelector((state) => state.auth)
  const isLoginPage = location.pathname === '/' || location.pathname === '/login'
  const isResetPasswordPage = location.pathname === '/reset-password'
  const isPasswordSuccessPage = location.pathname === '/password-success'

  // Set sidebar hidden immediately for authenticated pages (before first render)
  if (typeof document !== 'undefined' && !isLoginPage && !isResetPasswordPage && !isPasswordSuccessPage) {
    document.body.classList.add('vertical-collpsed')
  }

  // Fetch profile on app initialization if token exists but user is not loaded
  useEffect(() => {
    const initializeAuth = async () => {
      const activeRole = localStorage.getItem('activeRole')
      const storedToken = isOwnerRole(activeRole)
        ? localStorage.getItem('ownerToken')
        : activeRole === 'staff'
          ? localStorage.getItem('staffToken')
          : localStorage.getItem('token')

      // If we have a token but no user in Redux, fetch profile
      if (storedToken && !user) {
        try {
          console.log('Initializing auth: Fetching profile...', { role: activeRole })
          const profileResponse = isOwnerRole(activeRole)
            ? await authService.getOwnerProfile()
            : await authService.getProfile()
          console.log('Profile response on init:', profileResponse)

          // Owner profile has data directly, staff profile has data.user
          const userData = isOwnerRole(activeRole)
            ? profileResponse.data
            : profileResponse.data?.user

          if (profileResponse && profileResponse.success && userData) {
            dispatch(setCredentials({
              token: storedToken,
              user: userData,
              role: activeRole || 'staff'
            }))
            console.log('Profile loaded on app init:', userData)
          } else {
            console.warn('Invalid profile response, logging out')
            dispatch(logout())
          }
        } catch (error) {
          console.error('Failed to fetch profile on init:', error)
          // If 401, token is invalid, logout
          if (error.response?.status === 401) {
            dispatch(logout())
          }
        }
      } else if (storedToken && user) {
        // Token and user exist, ensure Redux is in sync
        dispatch(setCredentials({
          token: storedToken,
          user: user,
          role: activeRole || 'staff'
        }))
      }
    }

    if (!isLoginPage && !isResetPasswordPage && !isPasswordSuccessPage) {
      initializeAuth()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Run once on mount

  // Manage body classes based on current route
  useEffect(() => {
    if (isLoginPage || isResetPasswordPage || isPasswordSuccessPage) {
      document.body.classList.add('login')
      document.body.classList.remove('vertical-collpsed')
      document.body.setAttribute('data-sidebar', 'dark')
    } else {
      document.body.classList.remove('login')
      // Ensure sidebar is hidden by default on authenticated pages
      document.body.classList.add('vertical-collpsed')
    }

    return () => {
      document.body.classList.remove('login')
    }
  }, [isLoginPage, isResetPasswordPage, isPasswordSuccessPage])

  return (

    <div className={`App ${isLoginPage || isResetPasswordPage || isPasswordSuccessPage ? 'login' : ''}`}>
      {/* <Loader /> */}

      <SidebarProvider>

        <div className={isLoginPage || isResetPasswordPage || isPasswordSuccessPage ? 'wrapper' : ''}>
          <Routes>
            <Route
              path="/login"
              element={
                isAuthenticated ? (
                  user?.first_login === false ? (
                    <Navigate to="/reset-password" replace />
                  ) : (
                    <Navigate to="/dashboardhome" replace />
                  )
                ) : (
                  <Login />
                )
              }
            />
            <Route
              path="/"
              element={
                isAuthenticated ? (
                  user?.first_login === false ? (
                    <Navigate to="/reset-password" replace />
                  ) : (
                    <Navigate to="/dashboardHome" replace />
                  )
                ) : (
                  <Login />
                )
              }
            />
            <Route
              path="/reset-password"
              element={<ResetPassword />}
            />
            <Route
              path="/password-success"
              element={<PasswordSuccess />}
            />
            <Route
              path="/security-verification"
              element={<SecurityCodeVerification />}
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboardhome"
              element={
                <ProtectedRoute>
                  {/* <MarketAnalysis /> */}
                  <DashboardHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent-listing"
              element={
                <ProtectedRoute>
                  <AgentList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/transfer"
              element={
                <ProtectedRoute>
                  <Transfer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lords-notification"
              element={
                <ProtectedRoute>
                  <Notification />
                </ProtectedRoute>
              }
            />
            <Route
              path="/netexpouser"
              element={
                <ProtectedRoute>
                  <NetExposure />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bet-ticker"
              element={
                <ProtectedRoute>
                  <BetTicker />
                </ProtectedRoute>
              }
            />
            <Route
              path="/buffer"
              element={
                <ProtectedRoute>
                  <BufferPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/match-settlement"
              element={
                <ProtectedRoute>
                  <MatchSettlement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fancy-settlement"
              element={
                <ProtectedRoute>
                  <FancySettlement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/void-bets"
              element={
                <ProtectedRoute>
                  <VoidBets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mybets"
              element={
                <ProtectedRoute>
                  <BetList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/message-report"
              element={
                <ProtectedRoute>
                  <MessageReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/plreport"
              element={
                <ProtectedRoute>
                  <PLReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/transfer-statement"
              element={
                <ProtectedRoute>
                  <TransferStatement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/live-casino"
              element={
                <ProtectedRoute>
                  <LiveCasino />
                </ProtectedRoute>
              }
            />
            {/* Casino Game Routes */}
            <Route
              path="/lord-games/teen20c"
              element={
                <ProtectedRoute>
                  <TeenPatti20c />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/race17"
              element={
                <ProtectedRoute>
                  <Race17 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/dum10"
              element={
                <ProtectedRoute>
                  <Dum10 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/teensin"
              element={
                <ProtectedRoute>
                  <Baccarat29 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/teenmuf"
              element={
                <ProtectedRoute>
                  <TeenMuf />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/lucky15"
              element={
                <ProtectedRoute>
                  <Lucky15 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/teenjoker"
              element={
                <ProtectedRoute>
                  <TeenJoker />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/poker"
              element={
                <ProtectedRoute>
                  <Poker />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/poker20"
              element={
                <ProtectedRoute>
                  <Poker20 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/card32"
              element={
                <ProtectedRoute>
                  <Card32 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/card32eu"
              element={
                <ProtectedRoute>
                  <Card32B />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/aaa"
              element={
                <ProtectedRoute>
                  <Aaa />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/dt6"
              element={
                <ProtectedRoute>
                  <Dt6 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/sicbo"
              element={
                <ProtectedRoute>
                  <Sicbo />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/race20"
              element={
                <ProtectedRoute>
                  <Race20 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/queen"
              element={
                <ProtectedRoute>
                  <Queen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/abj"
              element={
                <ProtectedRoute>
                  <ABJ />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/dt202"
              element={
                <ProtectedRoute>
                  <DT202 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/dt20"
              element={
                <ProtectedRoute>
                  <Dt20 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/3cardj"
              element={
                <ProtectedRoute>
                  <CardJ />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/worli"
              element={
                <ProtectedRoute>
                  <Worli />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/worli2"
              element={
                <ProtectedRoute>
                  <WorliMatka />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/btable"
              element={
                <ProtectedRoute>
                  <BTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/ab20"
              element={
                <ProtectedRoute>
                  <AB20 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/baccarat2"
              element={
                <ProtectedRoute>
                  <Baccarat2 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/baccarat"
              element={
                <ProtectedRoute>
                  <Baccarat />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/lucky7eu"
              element={
                <ProtectedRoute>
                  <Lucky7B />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/lucky7"
              element={
                <ProtectedRoute>
                  <Lucky7 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/cmeter"
              element={
                <ProtectedRoute>
                  <Cmeter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/cmatch20"
              element={
                <ProtectedRoute>
                  <Cmatch20 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/war"
              element={
                <ProtectedRoute>
                  <CasinoWar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/dtl20"
              element={
                <ProtectedRoute>
                  <DragonTigerLion />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/teen9"
              element={
                <ProtectedRoute>
                  <Teen9 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/teen"
              element={
                <ProtectedRoute>
                  <Teen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/teen20"
              element={
                <ProtectedRoute>
                  <Teen20 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lord-games/teen8"
              element={
                <ProtectedRoute>
                  <Teen8 />
                </ProtectedRoute>
              }
            />
            {/* New Account List page (Users2) - primary route */}
            <Route
              path="/admin/users2"
              element={
                <ProtectedRoute>
                  <Users2 />
                </ProtectedRoute>
              }
            />
            {/* Backwards-compatible route so /admin/users also shows the new Account List */}
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="/createagent"
              element={
                <ProtectedRoute>
                  <AgentCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/user-details/:id"
              element={
                <ProtectedRoute>
                  <UserDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/create-account"
              element={
                <ProtectedRoute>
                  <CreateAccount />
                </ProtectedRoute>
              }
            />
            <Route
              path="/live-casino"
              element={
                <ProtectedRoute>
                  <LiveCasino />
                </ProtectedRoute>
              }
            />
            <Route
              path="/casino-tablelock"
              element={
                <ProtectedRoute>
                  <CasinoConfig />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users/insertuser"
              element={
                <ProtectedRoute>
                  <AddAccount />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/assign-agent"
              element={
                <ProtectedRoute>
                  <AssignAgent />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account-statement"
              element={
                <ProtectedRoute>
                  <AccountStatement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients-account-statement"
              element={
                <ProtectedRoute>
                  <ClientsAccountStatement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports/currentbets"
              element={
                <ProtectedRoute>
                  <CurrentBets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports/generalreport"
              element={
                <ProtectedRoute>
                  <GeneralReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports/livecasinoreport"
              element={
                <ProtectedRoute>
                  <CasinoReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports/profitloss"
              element={
                <ProtectedRoute>
                  <ProfitLoss />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports/casinoresult"
              element={
                <ProtectedRoute>
                  <CasinoResultReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports/casinoresult/:gameName"
              element={
                <ProtectedRoute>
                  <CasinoResultReport />
                </ProtectedRoute>
              }
            />
            {/* <Route
            path="/casino"
            element={
              <ProtectedRoute>
                <CasinoResultReport />
              </ProtectedRoute>
            }
          /> */}
            <Route
              path="/game"
              element={
                <ProtectedRoute>
                  <GameReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports/gamereport"
              element={
                <ProtectedRoute>
                  <GameReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings/userlock"
              element={
                <ProtectedRoute>
                  <GeneralLock />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/change-password"
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />
            <Route
              path="/secureauth"
              element={
                <ProtectedRoute>
                  <SecureAuth />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/createaccount"
              element={
                <ProtectedRoute>
                  <MultiLogin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/game-details/:sid/:gmid/:marketId"
              element={
                <ProtectedRoute>
                  <Sports />
                </ProtectedRoute>
              }
            />
            <Route
              path="*"
              element={
                <Navigate
                  to={isAuthenticated ? "/dashboardhome" : "/login"}
                  replace
                />
              }
            />
          </Routes>
        </div>
      </SidebarProvider>
    </div>
  )
}

export default App
